import React from 'react';
import { Typography, Container, Grid, Card, CardContent, Button, Box, Link, Chip } from '@mui/material';
import { Launch, Book, Code, School, YouTube, GitHub } from '@mui/icons-material';

const ResourcesPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-100 to-gray-200 pb-12">
      <Box className="py-16 bg-gradient-to-r from-purple-700 to-blue-500 text-white mb-8">
        <Container maxWidth="lg">
          <Typography variant="h2" component="h1" className="font-bold mb-4 text-center">
            Resources & Learning
          </Typography>
          <Typography variant="h6" className="text-center">
            Everything you need to master VIBE Coding with Rownd
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg">
        {/* Rownd Resources Section */}
        <Typography variant="h4" component="h2" className="mb-6 font-bold text-gray-800">
          Rownd Authentication Resources
        </Typography>
        <Grid container spacing={4} className="mb-12">
          <Grid item xs={12} md={6} lg={4}>
            <Card className="h-full shadow hover:shadow-lg transition-shadow duration-300">
              <CardContent className="flex flex-col h-full">
                <Box className="flex items-center mb-4">
                  <Book className="text-purple-600 mr-2" />
                  <Typography variant="h6" component="h3" className="font-bold text-gray-800">
                    Rownd Documentation
                  </Typography>
                </Box>
                <Typography variant="body1" className="text-gray-700 mb-4 flex-grow">
                  Comprehensive documentation for Rownd's authentication platform. Learn how to implement 
                  secure authentication in your applications with step-by-step guides.
                </Typography>
                <Chip label="Official" className="mb-4 bg-purple-100 text-purple-800 w-fit" />
                <Button 
                  variant="outlined" 
                  color="primary" 
                  endIcon={<Launch />}
                  href="https://docs.rownd.io"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-auto"
                >
                  View Documentation
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <Card className="h-full shadow hover:shadow-lg transition-shadow duration-300">
              <CardContent className="flex flex-col h-full">
                <Box className="flex items-center mb-4">
                  <Code className="text-purple-600 mr-2" />
                  <Typography variant="h6" component="h3" className="font-bold text-gray-800">
                    React SDK
                  </Typography>
                </Box>
                <Typography variant="body1" className="text-gray-700 mb-4 flex-grow">
                  Detailed guide on implementing Rownd in React applications. This resource includes 
                  code examples, component integration, and best practices.
                </Typography>
                <Chip label="Developer Guide" className="mb-4 bg-blue-100 text-blue-800 w-fit" />
                <Button 
                  variant="outlined" 
                  color="primary" 
                  endIcon={<Launch />}
                  href="https://docs.rownd.io/rownd/sdk-reference/web/react"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-auto"
                >
                  View React SDK
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <Card className="h-full shadow hover:shadow-lg transition-shadow duration-300">
              <CardContent className="flex flex-col h-full">
                <Box className="flex items-center mb-4">
                  <GitHub className="text-purple-600 mr-2" />
                  <Typography variant="h6" component="h3" className="font-bold text-gray-800">
                    Rownd GitHub
                  </Typography>
                </Box>
                <Typography variant="body1" className="text-gray-700 mb-4 flex-grow">
                  Access Rownd's open-source repositories, example projects, and contribute to the community. 
                  Find sample implementations and integration patterns.
                </Typography>
                <Chip label="Code Samples" className="mb-4 bg-gray-100 text-gray-800 w-fit" />
                <Button 
                  variant="outlined" 
                  color="primary" 
                  endIcon={<Launch />}
                  href="https://github.com/rownd"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-auto"
                >
                  View GitHub
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* VIBE Coding Resources */}
        <Typography variant="h4" component="h2" className="mb-6 font-bold text-gray-800">
          VIBE Coding Learning Resources
        </Typography>
        <Grid container spacing={4} className="mb-12">
          <Grid item xs={12} md={6} lg={4}>
            <Card className="h-full shadow hover:shadow-lg transition-shadow duration-300">
              <CardContent className="flex flex-col h-full">
                <Box className="flex items-center mb-4">
                  <Book className="text-blue-600 mr-2" />
                  <Typography variant="h6" component="h3" className="font-bold text-gray-800">
                    AI Coding Encyclopedia
                  </Typography>
                </Box>
                <Typography variant="body1" className="text-gray-700 mb-4 flex-grow">
                  Comprehensive guide to modern AI-assisted development techniques. Learn how to effectively 
                  prompt LLMs, review generated code, and optimize your VIBE Coding workflow.
                </Typography>
                <Chip label="Learning Guide" className="mb-4 bg-green-100 text-green-800 w-fit" />
                <Button 
                  variant="outlined" 
                  color="primary" 
                  endIcon={<Launch />}
                  href="https://en.wikipedia.org/wiki/Vibe_coding"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-auto"
                >
                  Read Guide
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <Card className="h-full shadow hover:shadow-lg transition-shadow duration-300">
              <CardContent className="flex flex-col h-full">
                <Box className="flex items-center mb-4">
                  <YouTube className="text-red-600 mr-2" />
                  <Typography variant="h6" component="h3" className="font-bold text-gray-800">
                    VIBE Coding Video Tutorials
                  </Typography>
                </Box>
                <Typography variant="body1" className="text-gray-700 mb-4 flex-grow">
                  Step-by-step video tutorials showing real examples of VIBE Coding in action. 
                  Watch experts build complete applications using AI assistance and secure authentication.
                </Typography>
                <Chip label="Video Series" className="mb-4 bg-red-100 text-red-800 w-fit" />
                <Button 
                  variant="outlined" 
                  color="primary" 
                  endIcon={<Launch />}
                  href="https://www.youtube.com/results?search_query=ai+assisted+coding"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-auto"
                >
                  Watch Tutorials
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <Card className="h-full shadow hover:shadow-lg transition-shadow duration-300">
              <CardContent className="flex flex-col h-full">
                <Box className="flex items-center mb-4">
                  <School className="text-blue-600 mr-2" />
                  <Typography variant="h6" component="h3" className="font-bold text-gray-800">
                    VIBE Coding Best Practices
                  </Typography>
                </Box>
                <Typography variant="body1" className="text-gray-700 mb-4 flex-grow">
                  Learn industry best practices for AI-assisted development. This guide covers prompt engineering, 
                  code review techniques, testing strategies, and security considerations.
                </Typography>
                <Chip label="Expert Guide" className="mb-4 bg-yellow-100 text-yellow-800 w-fit" />
                <Button 
                  variant="outlined" 
                  color="primary" 
                  endIcon={<Launch />}
                  href="https://github.com/topics/ai-assisted-coding"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-auto"
                >
                  View Best Practices
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Community Resources */}
        <Typography variant="h4" component="h2" className="mb-6 font-bold text-gray-800">
          Community & Support
        </Typography>
        <Grid container spacing={4} className="mb-12">
          <Grid item xs={12} md={6}>
            <Card className="h-full shadow hover:shadow-lg transition-shadow duration-300">
              <CardContent className="flex flex-col h-full">
                <Typography variant="h6" component="h3" className="font-bold mb-2 text-gray-800">
                  VIBE Coding Discord Community
                </Typography>
                <Typography variant="body1" className="text-gray-700 mb-4 flex-grow">
                  Join our active Discord community of VIBE Coding enthusiasts. Get help with your projects, 
                  share tips, and collaborate with other developers exploring AI-assisted development.
                </Typography>
                <Button 
                  variant="contained" 
                  color="primary" 
                  endIcon={<Launch />}
                  href="https://discord.gg/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-auto bg-purple-600 hover:bg-purple-700"
                >
                  Join Discord
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card className="h-full shadow hover:shadow-lg transition-shadow duration-300">
              <CardContent className="flex flex-col h-full">
                <Typography variant="h6" component="h3" className="font-bold mb-2 text-gray-800">
                  Rownd Customer Support
                </Typography>
                <Typography variant="body1" className="text-gray-700 mb-4 flex-grow">
                  Need help with your Rownd implementation? Access direct support from the Rownd team. 
                  Get answers to your questions, troubleshooting assistance, and integration guidance.
                </Typography>
                <Button 
                  variant="contained" 
                  color="primary" 
                  endIcon={<Launch />}
                  href="https://rownd.io/contact"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-auto bg-purple-600 hover:bg-purple-700"
                >
                  Contact Support
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Useful Links */}
        <Typography variant="h4" component="h2" className="mb-6 font-bold text-gray-800">
          Additional Resources
        </Typography>
        <Card className="shadow-lg mb-12">
          <CardContent>
            <Typography variant="h6" component="h3" className="font-bold mb-4 text-gray-800">
              Quick Links
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Link 
                  href="https://rownd.io" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-purple-600 hover:text-purple-800 flex items-center mb-2"
                >
                  <Launch className="mr-2 text-sm" /> Rownd Official Website
                </Link>
                <Link 
                  href="https://github.com/rownd" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-purple-600 hover:text-purple-800 flex items-center mb-2"
                >
                  <Launch className="mr-2 text-sm" /> Rownd GitHub Repositories
                </Link>
                <Link 
                  href="https://docs.rownd.io" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-purple-600 hover:text-purple-800 flex items-center mb-2"
                >
                  <Launch className="mr-2 text-sm" /> Rownd Documentation
                </Link>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Link 
                  href="https://en.wikipedia.org/wiki/Vibe_coding" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-purple-600 hover:text-purple-800 flex items-center mb-2"
                >
                  <Launch className="mr-2 text-sm" /> Wikipedia: VIBE Coding
                </Link>
                <Link 
                  href="https://github.com/topics/ai-coding-assistant" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-purple-600 hover:text-purple-800 flex items-center mb-2"
                >
                  <Launch className="mr-2 text-sm" /> AI Coding Assistants on GitHub
                </Link>
                <Link 
                  href="https://dev.to/t/aiassisted" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-purple-600 hover:text-purple-800 flex items-center mb-2"
                >
                  <Launch className="mr-2 text-sm" /> Dev.to AI-Assisted Articles
                </Link>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Link 
                  href="https://beta.reactjs.org/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-purple-600 hover:text-purple-800 flex items-center mb-2"
                >
                  <Launch className="mr-2 text-sm" /> React Documentation
                </Link>
                <Link 
                  href="https://mui.com/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-purple-600 hover:text-purple-800 flex items-center mb-2"
                >
                  <Launch className="mr-2 text-sm" /> Material-UI Framework
                </Link>
                <Link 
                  href="https://tailwindcss.com/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-purple-600 hover:text-purple-800 flex items-center mb-2"
                >
                  <Launch className="mr-2 text-sm" /> Tailwind CSS
                </Link>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Newsletter Signup */}
        <Box className="bg-gradient-to-r from-purple-700 to-blue-500 text-white p-8 rounded-lg text-center">
          <Typography variant="h4" component="h2" className="mb-4 font-bold">
            Stay Updated on VIBE Coding
          </Typography>
          <Typography variant="body1" className="mb-6 max-w-2xl mx-auto">
            Subscribe to our newsletter for the latest VIBE Coding techniques, Rownd integration tips, and exclusive resources.
          </Typography>
          <Button 
            variant="contained" 
            size="large"
            href="https://rownd.io"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white text-purple-700 hover:bg-gray-100"
          >
            Subscribe to Newsletter
          </Button>
        </Box>
      </Container>
    </div>
  );
};

export default ResourcesPage; 